import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getFormValues } from 'redux-form';
import axios from 'axios';
import { Grid, Field, Message, Flex } from 'nallian-shared-ui/lib/components';
import {
  SelectFieldValueComponent,
  FileFieldValueComponent,
  DatepickerFieldValueComponent,
  CheckBoxFieldValueComponent,
} from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { BYTES_IN_A_MEGABYTE } from 'nallian-shared-ui/lib/components/redux-form-semantic-ui/FileFieldValueComponent';
import { FormModal } from 'nallian-shared-ui/lib/modules';
import { showToaster } from 'nallian-shared-ui/lib/lib/showToaster';
import lookupService from 'nallian-shared-ui/lib/hoc/lookupService';
import apiRoutes from '../../../../../app/api/routes';
import { DOCUMENT_UPLOAD_FORM_NAME } from '../../../constants';

const EMPTY_UPLOAD_FORM = { documentType: '' };
const allowedFileTypes = ['.jpeg', '.jpg', '.png'];
const MAX_FILE_SIZE = 10 * BYTES_IN_A_MEGABYTE;

const fileToDataUrl = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const validate = (values, { t }) => {
  const errors = {};

  if (!values.documentType) {
    errors.documentType = t('documents.pleaseSelectType');
  }

  if (!values.file) {
    errors.file = t('documents.unsupportedFileType');
  }

  if (!values.expiresAt) {
    errors.expiresAt = t('documents.expirationDateRequired');
  } else {
    const expDate = new Date(values.expiresAt);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (expDate < today) {
      errors.expiresAt = t('documents.expirationDatePast');
    }

    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 10);
    if (expDate > maxDate) {
      errors.expiresAt = t('documents.expirationDateTooFar');
    }
  }

  if (!values.consent) {
    errors.consent = t('documents.consentCheckbox');
  }

  return errors;
};

const DocumentUploadModal = ({
  onClose,
  onUploadComplete,
  securedDocumentTypesFetch,
  initialData,
}) => {
  const { t } = useTranslation(['app', 'shared']);
  const [uploading, setUploading] = useState(false);
  const formValues = useSelector(state => getFormValues(DOCUMENT_UPLOAD_FORM_NAME)(state));
  const consentChecked = !!formValues?.consent;

  const documentTypeOptions = useMemo(
    () =>
      (securedDocumentTypesFetch?.value?.documentTypes || []).map(dt => ({
        key: dt.id,
        value: dt.id,
        text: dt.name,
      })),
    [securedDocumentTypesFetch]
  );

  const minDate = useMemo(() => new Date(), []);
  const maxDate = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 10);
    return d;
  }, []);

  const handleSubmit = useCallback(
    async values => {
      try {
        setUploading(true);

        // 1. Analyze document for redaction zones (no upload yet)
        const analyzeFormData = new FormData();
        analyzeFormData.append('file', values.file);
        analyzeFormData.append('documentType', values.documentType);

        const analyzeResponse = await axios.post(apiRoutes.analyzeDocument(), analyzeFormData, {
          headers: { 'content-type': undefined },
        });
        const redactionZones = analyzeResponse.data.redactionZones || [];

        // 2. Convert file to data URL for client-side redaction
        const imageDataUrl = await fileToDataUrl(values.file);

        // 3. Pass data to preview - actual upload happens on confirm
        onUploadComplete({
          documentType: values.documentType,
          expiresAt: values.expiresAt,
          redactionZones,
          imageDataUrl,
          fileName: values.file.name,
        });
      } catch (err) {
        const data = err?.response?.data;
        const errorMessage = Array.isArray(data)
          ? data.map(c => (c.errors || []).join(' ')).join(' ')
          : t('documents.analyzeFailed');
        showToaster('error', errorMessage || t('documents.analyzeFailed'));
      } finally {
        setUploading(false);
      }
    },
    [onUploadComplete, t]
  );

  return (
    <FormModal
      t={t}
      header={t('documents.upload')}
      dataLoading={securedDocumentTypesFetch?.pending}
      submitProcessing={uploading}
      onSubmit={handleSubmit}
      data={initialData || EMPTY_UPLOAD_FORM}
      validate={validate}
      form={DOCUMENT_UPLOAD_FORM_NAME}
      cancelRoute=""
      onCancel={onClose}
      cancelButtonContent={t('cancel')}
      saveButtonContent={t('save')}
      saveDisabled={!consentChecked}
      modalSize="small"
    >
      <Grid>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <Field
            fluid
            search
            label={t('documents.type')}
            name="documentType"
            placeholder={t('documents.selectType')}
            options={documentTypeOptions}
            loading={securedDocumentTypesFetch?.pending}
            valueComponent={SelectFieldValueComponent}
          />
        </Grid.Column>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <Field
            fluid
            label={t('documents.file')}
            name="file"
            accept={allowedFileTypes}
            maxSize={MAX_FILE_SIZE}
            valueComponent={FileFieldValueComponent}
          />
        </Grid.Column>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <Field
            fluid
            showMonthDropdown
            showYearDropdown
            label={t('documents.expirationDate')}
            name="expiresAt"
            minDate={minDate}
            maxDate={maxDate}
            dateFormat="dd/MM/yyyy"
            valueComponent={DatepickerFieldValueComponent}
          />
          <Flex margin="-0.5rem 0 0">
            <small>{t('documents.expirationDateHelperText')}</small>
          </Flex>
        </Grid.Column>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <Message info content={t('documents.consentWarning')} />
          <Field
            fluid
            hasLabel={false}
            componentLabel={t('documents.consentCheckbox')}
            name="consent"
            valueComponent={CheckBoxFieldValueComponent}
          />
        </Grid.Column>
      </Grid>
    </FormModal>
  );
};

DocumentUploadModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onUploadComplete: PropTypes.func.isRequired,
  securedDocumentTypesFetch: PropTypes.object.isRequired,
  initialData: PropTypes.object,
};

export default lookupService({
  resource: 'securedDocumentTypes',
  url: apiRoutes.securedDocumentTypes(),
})(DocumentUploadModal);
